import { LineType, registerIndicator, getSupportedIndicators } from 'klinecharts'
import type { Chart, IndicatorCreate, IndicatorStyle, IndicatorTemplate, SmoothLineStyle } from 'klinecharts'
import { EMPTY_TOOLTIP } from './chartStyles'
import { getIndicator, asOverlayId } from './types'
import type { IndicatorOption, IndicatorSettings } from './types'
import { TAIL_SAFE_INDICATORS, unwrapCalc, withTailCalc } from './tailIndicatorCalc'

/**
 * Indicator create/update path shared by the picker, the settings dialog and the
 * legends, so they can never diverge. Main-pane indicators overlay the candles
 * ('candle_pane'); the rest open their own sub-pane.
 *
 * Multiple instances of the same indicator are supported. klinecharts addresses
 * an instance ONLY by (paneId, name) and rejects a same-name create on one pane,
 * so each instance gets a unique ENGINE name: the base name for the first, a
 * registered clone ("MA#2", "MA#3", …) for duplicates — see mintEngineName /
 * ensureIndicatorClone. The catalog/base name stays the display + persistence
 * identity. Volume is special chrome (faded bars on the price pane — see
 * volumeOverlay.ts), not a normal instance.
 */

/** Default per-line colour palette (klinecharts' own). */
export const DEFAULT_LINE_COLORS = ['#FF9600', '#935EBD', '#1677FF', '#E11D74', '#01C5C4']

export function defaultSettings(): IndicatorSettings {
  return {
    params: [], // seeded from the engine's defaults after creation
    colors: [...DEFAULT_LINE_COLORS],
    thickness: 1,
    visible: true,
    lastValueMark: true, // "Labels on price scale"
    valuesInLegend: true, // "Values in status line"
  }
}

/** Human labels for the settings inputs of the common indicators. */
const PARAM_LABELS: Record<string, string[]> = {
  MA: ['Period 1', 'Period 2', 'Period 3', 'Period 4'],
  EMA: ['Period 1', 'Period 2', 'Period 3'],
  SMA: ['Length', 'Weight'],
  BOLL: ['Length', 'StdDev'],
  RSI: ['Period 1', 'Period 2', 'Period 3'],
  MACD: ['Fast', 'Slow', 'Signal'],
  KDJ: ['K', 'D', 'J'],
  CCI: ['Length'],
  WR: ['Period 1', 'Period 2', 'Period 3'],
  BIAS: ['Period 1', 'Period 2', 'Period 3'],
}

export function paramLabel(name: string, index: number): string {
  return PARAM_LABELS[name]?.[index] ?? `Param ${index + 1}`
}

// RSI shows 70/50/30 reference bands as locked dashed horizontal overlays on its pane.
const RSI_BANDS = [70, 50, 30]
const BAND_COLOR = 'rgba(136,136,136,0.45)'

/** Smallest free engine name for `base` given the names already held. */
export function mintEngineName(base: string, taken: Iterable<string>): string {
  const set = new Set(taken)
  if (!set.has(base)) return base
  for (let k = 2; ; k++) if (!set.has(`${base}#${k}`)) return `${base}#${k}`
}

/**
 * Register `cloneName` as a copy of a live indicator's template. Built-in
 * templates aren't exported by klinecharts, but their calc/regenerateFigures
 * are `this`-independent and readable off a live instance. No-op when already
 * registered — the registry is module-global and survives chart disposal.
 */
export function ensureIndicatorClone(
  chart: Chart,
  source: { paneId: string; engineName: string },
  cloneName: string,
  shortName: string,
): boolean {
  if (getSupportedIndicators().includes(cloneName)) return true
  const live = getIndicator(chart, source.paneId, source.engineName)
  if (!live) return false
  registerIndicator({
    name: cloneName,
    shortName,
    series: live.series,
    calcParams: live.calcParams,
    precision: live.precision,
    shouldOhlc: live.shouldOhlc,
    shouldFormatBigNumber: live.shouldFormatBigNumber,
    minValue: live.minValue,
    maxValue: live.maxValue,
    figures: live.figures,
    // The engine's own calc, not the tail-only wrapper a live instance may carry: the clone's
    // instance is wrapped when it is created, like any other (applyIndicator).
    calc: unwrapCalc(live.calc),
    ...(live.regenerateFigures ? { regenerateFigures: live.regenerateFigures } : {}),
  } as IndicatorTemplate)
  return true
}

/**
 * Apply per-instance line styles, sliced to the indicator's ACTUAL figure count —
 * passing more line styles than the indicator has figures makes klinecharts throw.
 */
export function styleIndicator(
  chart: Chart,
  name: string,
  paneId: string,
  s: IndicatorSettings,
): void {
  const ind = getIndicator(chart, paneId, name)
  const n = ind?.figures?.length || 1
  // Full line-style objects — overrideIndicator replaces (not deep-merges) each
  // line, so a partial {color,size} would drop `dashedValue` and crash the
  // engine's line-draw merge.
  const lines: SmoothLineStyle[] = (s.colors ?? DEFAULT_LINE_COLORS)
    .slice(0, n)
    .map((color) => ({
      style: LineType.Solid,
      smooth: false,
      size: s.thickness ?? 1,
      color,
      dashedValue: [2, 2],
    }))
  // "Labels on price scale" needs BOTH show + text.show (the engine gates on both).
  const showMark = s.lastValueMark !== false
  const markText = {
    show: showMark,
    ...(s.textColor ? { color: s.textColor } : {}),
  } as IndicatorStyle['lastValueMark']['text']
  chart.overrideIndicator(
    {
      name,
      styles: { lines, lastValueMark: { show: showMark, text: markText } },
    },
    paneId,
  )
}

export interface AppliedIndicator {
  paneId: string
  bandIds: string[]
  /** Settings with `params` seeded from the engine defaults when empty. */
  settings: IndicatorSettings
}

/**
 * Create one indicator instance with the given settings. Params are omitted at
 * creation when unset so the engine's own defaults apply; the live values are
 * then read back to seed the settings dialog. `engineName` is the unique engine
 * identity (base name or a registered clone — see mintEngineName).
 */
export function applyIndicator(
  chart: Chart,
  option: IndicatorOption,
  settings?: IndicatorSettings,
  engineName: string = option.name,
): AppliedIndicator | null {
  const s = settings ?? defaultSettings()
  const create: IndicatorCreate = {
    name: engineName,
    visible: s.visible !== false,
    createTooltipDataSource: EMPTY_TOOLTIP,
  }
  if (s.params.length > 0) create.calcParams = s.params
  const paneId =
    option.pane === 'main'
      ? chart.createIndicator(create, true, { id: 'candle_pane' })
      : chart.createIndicator(create) // own fresh pane
  if (!paneId) return null

  const live = getIndicator(chart, paneId, engineName)
  // A replay updates the last bar many times a second; recalculate only it (tailIndicatorCalc.ts).
  if (live?.calc && TAIL_SAFE_INDICATORS.has(option.name)) {
    chart.overrideIndicator({ name: engineName, calc: withTailCalc(live.calc) }, paneId)
  }
  const params =
    s.params.length > 0
      ? s.params
      : (live?.calcParams ?? []).filter((p): p is number => typeof p === 'number')
  const seeded: IndicatorSettings = { ...s, params }

  styleIndicator(chart, engineName, paneId, seeded)

  let bandIds: string[] = []
  if (option.name === 'RSI') {
    bandIds = RSI_BANDS.map((v) =>
      asOverlayId(
        chart.createOverlay(
          {
            name: 'horizontalStraightLine',
            points: [{ value: v }],
            lock: true,
            styles: { line: { style: LineType.Dashed, color: BAND_COLOR, size: 1 } },
            onRightClick: () => true,
          },
          paneId,
        ),
      ),
    ).filter((id): id is string => id != null)
  }
  return { paneId, bandIds, settings: seeded }
}

/** Push settings changes to a live indicator (used by the settings dialog). */
export function updateIndicatorStyles(
  chart: Chart,
  inst: { engineName: string; paneId: string; settings: IndicatorSettings },
): void {
  chart.overrideIndicator(
    {
      name: inst.engineName,
      calcParams: inst.settings.params,
      visible: inst.settings.visible !== false,
    },
    inst.paneId,
  )
  styleIndicator(chart, inst.engineName, inst.paneId, inst.settings)
}
